import React, { useLayoutEffect, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { IntlProvider } from "react-intl";
import { useDispatch, useSelector, connect } from "react-redux";
import { ThemeProvider } from "styled-components";
import styled from "styled-components/macro";
import ContentValue from "../../constants/lang/en.json";
import vmTheme from "../../constants/theme";
import { getCredits, getUserDetails, setProjectDetails, getProjectInfo } from "../../redux/actions/projectUtils";
import { Wrapper } from "./shorts-components";
import Header from "../header/header";
import ShortsToolbar from "./shorts-toolbar";
import Loader from "../../common-components/Loader";
import openUrl from "../../helper/openUrl";
import withNotify from "../../helper/hoc/withNotify";
import Toast from "../../common-components/Toast";
import AppConfig from "../../constants/config";
import { getIPData, getPlanDetails, getPresetItems, setShortsPlayer } from "../../redux/actions/appUtils";
import ShortsContextProvider from "./shortsContextApi";
import ShortsPricingModal from "./shorts-pricing-modal";
import PaymentModal from "../payments/paymentModal";
import PriceModal from "../payments/priceModal";
import AgentReviewDrawer from "./agent-review-drawer";
import ShortsListModal from "./shorts-list-modal";
import { isAnimakerEmail } from "../../helper/isValidEmail";
import TRACKERS from "../../constants/trackers";
import trackerEvents from "../../helper/trackerEvents";
import ErrorModal from "../../common-components/ErrorModal";

const { ENVIRONMENT, IS_PROD } = AppConfig;

const ShortsContainer = styled.div`
  display: flex;
  height: calc(100vh - 104px);
  position: relative;

  @media screen and (max-width: 767px) {
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: calc(100dvh - 120px);
  }
`;

const ShortsContent = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: auto;
    overflow: hidden;
  }
`;

const Shorts = (props) => {
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.app.get("theme"));
  const projectDetails = useSelector((state) => state.projectDetails);
  const userDetails = useSelector((state) => state.userDetails);
  const [debugData, setDebugData] = useState(null);
  const [isAgentReviewOpen, setIsAgentReviewOpen] = useState(false);
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: '',
    message: '',
  });

  const [ownerUserId, setOwnerUserId] = useState(null);
  const hasTrackedPageView = useRef(false);

  const { params = { id: "" } } = props.match;

  useLayoutEffect(() => {
    // Load geo IP immediately so restriction checks don't use Boardman/US default
    props.getIPData().catch(() => { });

    getUserDetails().then((response) => {
      props.getPresetItem();
      props.getPlanDetails();
      props.setCredits();
      dispatch(setProjectDetails(params.id, response, false)).then((res) => {
        const { permission, project } = res;
        const hasShorts = project.shorts && Object.keys(project.shorts).length !== 0;
        if (permission === -1) {
          props.notify.warn("You don't have access to this project.");
          openUrl(`${window.location.origin}/preview/${params.id}`, true);
        } else if (!hasShorts) {
          setErrorModal({
            show: true,
            title: 'No shorts Available',
            message: 'This project does not have Shorts.'
          });
          return;
        }
        dispatch(setShortsPlayer({ isShortsPlayer: true }));
        props.setProjectData(params.id).then((res) => {
          setOwnerUserId(res.user_id);
        });
      }).catch(() => {
        openUrl(`${window.location.origin}${ENVIRONMENT === "LOCAL" ? "" : "/editproject"}/${params.id}`, true);
      });
    }).catch(() => {
      dispatch(setProjectDetails(params.id, {}, false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch debug data for the project
  useEffect(() => {
    if ((!IS_PROD || isAnimakerEmail(userDetails.email)) && ownerUserId && params.id) {
      const debugUrl = `https://dash.animaker.com/a/u/${ownerUserId}/lftsf/${params.id}/agent_debug.json`;
      fetch(debugUrl)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.execution_trace) {
            setDebugData(data);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch debug data:", err);
        });
    }
  }, [ownerUserId, params.id, userDetails.email]);

  useEffect(() => {
    if (hasTrackedPageView.current || !params.id) return;
    if (projectDetails.get("shorts").size === 0) return;

    hasTrackedPageView.current = true;
    trackerEvents(TRACKERS.LTSF_PROJECT_FIRST_VIEWED, {
      projectId: params.id,
      isBoth: 1
    });
  }, [params.id, projectDetails]);

  return (
    <IntlProvider locale="en" messages={{ ...ContentValue }}>
      <ThemeProvider theme={vmTheme[theme]}>
        <ShortsContextProvider>
          <Header isShorts={true} />
          <PaymentModal />
          <PriceModal />
          <ShortsPricingModal />
          <ShortsToolbar isShorts={true} />

          <ShortsContainer>
            {/* Left Content - Shorts */}
            <ShortsContent>
              <Wrapper>
                {errorModal.show ? (
                  <ErrorModal
                    title={errorModal.title}
                    message={errorModal.message}
                    primaryButtonText="Got it!"
                    onPrimaryButtonClick={() => {
                      openUrl(
                        `${window.location.origin}${ENVIRONMENT === "LOCAL" ? "" : "/editproject"}/${params.id}`,
                        true,
                      );
                    }}
                    onCloseModal={() => {
                      setErrorModal({
                        show: false,
                        title: "",
                        message: "",
                      });
                    }}
                  />
                ) : projectDetails.get("shorts").size > 0 ? (
                  <ShortsListModal
                    shorts={projectDetails.get("shorts")}
                    projectDetails={projectDetails}
                    userDetails={userDetails}
                    params={params}
                  />
                ) : (
                  <Loader disableProgress={true} />
                )}
                <Toast />
              </Wrapper>
            </ShortsContent>
          </ShortsContainer>

          {(!IS_PROD || isAnimakerEmail(userDetails.email)) && debugData && (
            <AgentReviewDrawer
              isOpen={isAgentReviewOpen}
              onToggle={() => setIsAgentReviewOpen((open) => !open)}
              onClose={() => setIsAgentReviewOpen(false)}
              executionTrace={debugData.execution_trace}
            />
          )}
        </ShortsContextProvider>
      </ThemeProvider>
    </IntlProvider>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCredits: bindActionCreators(getCredits, dispatch),
    getPresetItem: bindActionCreators(getPresetItems, dispatch),
    getIPData: bindActionCreators(getIPData, dispatch),
    getPlanDetails: bindActionCreators(getPlanDetails, dispatch),
    setProjectData: bindActionCreators(getProjectInfo, dispatch)
  };
};

Shorts.propTypes = {
  match: PropTypes.object,
  notify: PropTypes.func,
  setCredits: PropTypes.func,
  getIPData: PropTypes.func,
  getPresetItem: PropTypes.func,
  getPlanDetails: PropTypes.func,
  setProjectData: PropTypes.func
};

export default connect(null, mapDispatchToProps)(withNotify(Shorts));
